import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import {
  AddendumTemplate,
  AddendumTemplateType,
  useCreateAddendumTemplateMutation,
  useDeleteAddendumTemplateMutation,
  useGetAddendumTemplatesQuery,
  useUpdateAddendumTemplateMutation,
} from '@/apis/extended/admin/addendumTemplateApi';

const TEMPLATE_TYPES: AddendumTemplateType[] = [
  'extension',
  'repair_request',
  'name_change',
  'general',
  'amendment',
  'attachment',
  'other',
];

const extractPlaceholders = (body: string) => {
  const matches = body.match(/{{\s*([a-zA-Z0-9_]+)\s*}}/g) || [];
  const tokens = matches.map((m) => m.replace(/{{|}}/g, '').trim());
  return Array.from(new Set(tokens));
};

const emptyForm = {
  type: 'general' as AddendumTemplateType,
  title: '',
  body: '',
  isActive: true,
};

const AddendumTemplatesPanel: React.FC = () => {
  const { data: templates, isLoading } = useGetAddendumTemplatesQuery();
  const [createTemplate, { isLoading: isCreating }] =
    useCreateAddendumTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] =
    useUpdateAddendumTemplateMutation();
  const [deleteTemplate] = useDeleteAddendumTemplateMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AddendumTemplate | null>(null);
  const [form, setForm] = useState(emptyForm);

  const placeholders = useMemo(
    () => extractPlaceholders(form.body),
    [form.body]
  );

  const handleOpenCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleOpenEdit = (template: AddendumTemplate) => {
    setEditing(template);
    setForm({
      type: template.type,
      title: template.title,
      body: template.body,
      isActive: template.isActive,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.body) return;
    if (editing) {
      await updateTemplate({
        templateId: editing._id,
        update: {
          ...form,
          placeholders,
        },
      }).unwrap();
    } else {
      await createTemplate({
        ...form,
        placeholders,
      }).unwrap();
    }
    setDialogOpen(false);
  };

  const handleDelete = async (templateId: string) => {
    if (!window.confirm('Delete this template?')) return;
    await deleteTemplate(templateId).unwrap();
  };

  return (
    <Card variant="outlined">
      <CardHeader
        title="Addendum Templates"
        action={
          <Button variant="contained" onClick={handleOpenCreate}>
            New Template
          </Button>
        }
      />
      <CardContent>
        {isLoading && <Typography>Loading templates...</Typography>}
        {!isLoading && (!templates || templates.length === 0) && (
          <Alert severity="info">No addendum templates yet.</Alert>
        )}
        {!isLoading && templates && templates.length > 0 && (
          <Grid container spacing={2}>
            {templates.map((template) => (
              <Grid item xs={12} md={6} key={template._id}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <Typography variant="h6">{template.title}</Typography>
                      <Chip
                        size="small"
                        label={template.type.replace('_', ' ')}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {template.body.slice(0, 140)}
                      {template.body.length > 140 ? '…' : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <Chip
                        size="small"
                        color={template.isActive ? 'success' : 'default'}
                        label={template.isActive ? 'Active' : 'Inactive'}
                      />
                      {template.placeholders?.length ? (
                        <Chip
                          size="small"
                          label={`${template.placeholders.length} placeholders`}
                        />
                      ) : null}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenEdit(template)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleDelete(template._id)}
                      >
                        Delete
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth>
        <DialogTitle>
          {editing ? 'Edit Template' : 'Create Template'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={form.type}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      type: e.target.value as AddendumTemplateType,
                    }))
                  }
                >
                  {TEMPLATE_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Title"
                value={form.title}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, title: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={6}
                label="Template Body"
                value={form.body}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, body: e.target.value }))
                }
                placeholder="Use {{placeholder}} tokens, e.g. {{buyerName}}"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Switch
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      isActive: e.target.checked,
                    }))
                  }
                />
                <Typography variant="body2">Active</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Detected placeholders:{' '}
                {placeholders.length ? placeholders.join(', ') : 'None'}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isCreating || isUpdating || !form.title || !form.body}
          >
            Save Template
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default AddendumTemplatesPanel;
